<script lang="ts" setup>
import { segmentation } from '@thecompaniesapi/shared/client'

const { getCompanyAttributes, getMetaAttributes, getSortAttributes } = useApiAttributes()
const { version } = useApiDoc()
const { t } = useI18n()
const api = useApi()
const localePath = useLocalePath()

const { data: response, execute, status } = useAsyncData(
  'doc:companies-search-prompt',
  () => api.companies.fetchByPrompt({ prompt: segmentation.freeQueries.prompt }),
  { immediate: false },
)

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: `/companies/by-prompt?prompt=${segmentation.freeQueries.prompt}`,
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.prompt'),
    example: `…/companies/by-prompt?prompt=${segmentation.freeQueries.prompt}`,
    name: 'prompt',
    required: true,
    type: 'string',
  },
  {
    description: t('attributes.listsToExclude'),
    example: '/companies/by-prompt?listsToExclude=3920,3925',
    name: 'listsToExclude',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.page'),
    example: '/companies/by-prompt?page=1',
    name: 'page',
    optional: true,
    type: 'number',
  },
  {
    description: t('attributes.similarity'),
    example: '/companies/by-prompt?similarity=0.75',
    name: 'similarity',
    optional: true,
    type: 'number',
  },
  {
    description: t('attributes.simplified'),
    example: '/companies/by-prompt?simplified=true',
    name: 'simplified',
    optional: true,
    type: 'boolean',
  },
  {
    description: t('attributes.size'),
    example: '/companies/by-prompt?size=10',
    name: 'size',
    optional: true,
    type: 'number',
  },
  ...getSortAttributes({ endpoint: '/companies/by-prompt' }),
]

const responseAttributes = computed((): ApiAttribute[] => {
  return [
    {
      attributes: getCompanyAttributes({ version: version.value }),
      description: t('response.companies'),
      link: {
        label: t('globals.browseAllDatapoints'),
        to: localePath({
          name: 'api-slug',
          params: { slug: 'datapoints' },
        }),
      },
      name: 'companies',
      type: 'array',
    },
    getMetaAttributes(),
  ]
})

function onVisibilityChange(visibility: boolean) {
  if (visibility && !response.value) {
    execute()
  }
}
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "listsToExclude": "Comma-separated IDs of your lists containing companies to exclude from the results.",
      "page": "The page to fetch (default to 1).",
      "prompt": "The prompt our AI model will use to search for companies.",
      "similarity": "The similarity threshold to use for the search (between 0 and 2). Default to 1.875.",
      "simplified": "Whether to return a simplified version of the company profile. Default to false.",
      "size": "The number of companies to be returned (between 1 and 25). Default to 10."
    },
    "response": {
      "companies": "The list of companies matching your prompt."
    }
  },
  "es": {
    "attributes": {
      "listsToExclude": "Los identificadores separados por comas de tus listas que contienen las empresas a excluir de los resultados.",
      "page": "La página a recuperar (por defecto 1).",
      "prompt": "El prompt que nuestro modelo de IA utilizará para buscar empresas.",
      "similarity": "El umbral de similitud a utilizar para la búsqueda (entre 0 y 1). Por defecto a 0.75.",
      "simplified": "Si se debe devolver una versión simplificada del perfil de la empresa. Por defecto a false.",
      "size": "El número de empresas a devolver (entre 1 y 25). Por defecto a 10."
    },
    "response": {
      "companies": "La lista de empresas que coinciden con tu prompt."
    }
  },
  "fr": {
    "attributes": {
      "listsToExclude": "Les identifiants séparés par des virgules de vos listes contenant les entreprises à exclure des résultats.",
      "page": "La page à récupérer (par défaut 1).",
      "prompt": "Le prompt que notre modèle d'IA utilisera pour rechercher des entreprises.",
      "similarity": "Le seuil de similarité à utiliser pour la recherche (entre 0 et 1). Par défaut à 0.75.",
      "simplified": "S'il faut retourner une version simplifiée du profil de l'entreprise. Par défaut à false.",
      "size": "Le nombre d'entreprises à retourner (entre 1 et 25). Par défaut à 10."
    },
    "response": {
      "companies": "La liste des entreprises correspondant à votre prompt."
    }
  }
}
</i18n>
